import { PlanId, Currency } from './pricing'
import type { EffectivePlanSource, PaymentMethodDto } from './subscription-api'

const getSubscriptionApi = async () => {
  const mod = await import('./subscription-api')
  return mod.subscriptionApi
}

/**
 * Stripe Integration (Backend API)
 * 
 * This module provides functions for Stripe subscription management.
 * All operations now go through the backend API for security and proper webhook handling.
 */

// Keep Stripe public key for potential future use (e.g., Stripe Elements)
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

export interface CheckoutSessionParams {
  planId: Exclude<PlanId, 'free'>
  currency: Currency
  successUrl: string
  cancelUrl: string
  trialPeriodDays?: number
  offerCode?: 'founder_2026'
}

/**
 * Create a subscription checkout session
 * Redirects user to Stripe checkout page
 */
export async function createCheckoutSession(params: CheckoutSessionParams): Promise<string> {
  const subscriptionApi = await getSubscriptionApi()
  const response = await subscriptionApi.createCheckout(params)
  return response.checkoutUrl
}

export interface BillingPortalParams {
  returnUrl: string
}

/**
 * Create a billing portal session
 * Redirects user to Stripe billing portal for subscription management
 */
export async function createBillingPortalSession(params: BillingPortalParams): Promise<string> {
  const subscriptionApi = await getSubscriptionApi()
  const response = await subscriptionApi.createPortalSession(params.returnUrl)
  return response.portalUrl
}

/**
 * Legacy interface for backward compatibility
 */
export interface SubscriptionStatus {
  provider?: 'stripe' | 'hotmart' | 'none'
  /** Whether a real, currently active/trialing billing subscription exists (Stripe or Hotmart). */
  hasActiveBillingSubscription: boolean
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'inactive' | string
  currentPeriodEnd: Date | null
  cancelAtPeriodEnd: boolean
  planId: PlanId
  effectivePlanId?: PlanId
  effectivePlanSource?: EffectivePlanSource
  effectivePlanAssignedAt?: Date | null
  planAssignmentReason?: string | null
  hasStripeCustomer: boolean
  subscriptionPlanId?: PlanId
  isInTrial?: boolean
  daysUntilRenewal?: number
  isFounder: boolean
  founderSince?: Date | null
  billingInterval?: 'day' | 'week' | 'month' | 'year' | null
  managedExternally?: boolean
  manageUrl?: string | null
  lastWebhookAt?: Date | null
  graceEndsAt?: Date | null
  buyerEmail?: string | null
  buyerName?: string | null
}

/**
 * Get current subscription status.
 *
 * This always resolves the tenant's effective PinDay plan (from `effectivePlanId`),
 * even when there is no active Stripe/Hotmart billing subscription - e.g. a plan
 * granted manually via the SaaS Admin console. `hasActiveBillingSubscription` is the
 * flag to check for "is there a real billing subscription behind this plan".
 *
 * Throws on network/auth/API failures rather than swallowing them - callers must
 * catch and handle load failures explicitly (e.g. subscription-store's fetchSubscription
 * preserves the previous state and surfaces an error instead of silently caching an
 * "unknown" status as if it were a confirmed free/no-subscription state).
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  const subscriptionApi = await getSubscriptionApi()
  const response = await subscriptionApi.getCurrentSubscription()

  const planId = (response.effectivePlanId as PlanId) || (response.planId as PlanId) || 'free'

  return {
    provider: response.provider,
    hasActiveBillingSubscription: response.hasSubscription,
    status: response.hasSubscription ? (response.status || 'active') : 'inactive',
    currentPeriodEnd: response.currentPeriodEnd ? new Date(response.currentPeriodEnd) : null,
    cancelAtPeriodEnd: response.cancelAtPeriodEnd || false,
    planId,
    effectivePlanId: planId,
    effectivePlanSource: response.effectivePlanSource,
    effectivePlanAssignedAt: response.effectivePlanAssignedAt ? new Date(response.effectivePlanAssignedAt) : null,
    planAssignmentReason: response.planAssignmentReason ?? null,
    hasStripeCustomer: response.hasStripeCustomer ?? false,
    subscriptionPlanId: (response.subscriptionPlanId as PlanId) || undefined,
    isInTrial: response.isInTrial,
    daysUntilRenewal: response.daysUntilRenewal,
    isFounder: response.isFounder,
    founderSince: response.founderSince ? new Date(response.founderSince) : null,
    billingInterval: response.billingInterval,
    managedExternally: response.managedExternally,
    manageUrl: response.manageUrl || null,
    lastWebhookAt: response.lastWebhookAt ? new Date(response.lastWebhookAt) : null,
    graceEndsAt: response.graceEndsAt ? new Date(response.graceEndsAt) : null,
    buyerEmail: response.buyerEmail || null,
    buyerName: response.buyerName || null,
  }
}

/**
 * Cancel subscription
 * @param cancelAtPeriodEnd If true, cancel at end of billing period. If false, cancel immediately.
 */
export async function cancelSubscription(cancelAtPeriodEnd: boolean = true): Promise<void> {
  const subscriptionApi = await getSubscriptionApi()
  await subscriptionApi.cancelSubscription(cancelAtPeriodEnd)
}

/**
 * Update subscription plan (upgrade or downgrade)
 */
export async function updateSubscriptionPlan(
  newPlanId: Exclude<PlanId, 'free'>,
  prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice'
): Promise<void> {
  const subscriptionApi = await getSubscriptionApi()
  const baseUrl = window.location.origin
  await subscriptionApi.updateSubscription({
    newPlanId,
    prorationBehavior,
    successUrl: `${baseUrl}/dashboard?upgrade=success&plan=${newPlanId}`,
    cancelUrl: `${baseUrl}/pricing?upgrade=canceled`,
  })
}

/**
 * Manually sync subscription status from Stripe
 * Useful for recovering from missed webhooks
 */
export async function syncSubscriptionStatus(): Promise<void> {
  const subscriptionApi = await getSubscriptionApi()
  await subscriptionApi.syncSubscription()
}

/**
 * List invoices for current tenant
 */
export async function listInvoices() {
  const subscriptionApi = await getSubscriptionApi()
  return await subscriptionApi.listInvoices()
}

/**
 * Get the tenant's default payment method, if any Stripe customer exists
 */
export async function getPaymentMethod(): Promise<PaymentMethodDto | null> {
  const subscriptionApi = await getSubscriptionApi()
  const response = await subscriptionApi.getPaymentMethod()
  return response.paymentMethod
}

export type { InvoiceDto, SubscriptionStatusResponse, EffectivePlanSource, PaymentMethodDto } from './subscription-api'

// Export Stripe public key for potential future use
export { STRIPE_PUBLIC_KEY }
